'use client';

import { useEffect } from 'react';

/**
 * Falls the app back to system fonts when the brand webfont loads but renders
 * nothing.
 *
 * A corrupted macOS font cache makes CoreText hand WebKit a face with no
 * drawable glyphs: the request succeeds, `document.fonts` reports it loaded, and
 * the text is simply invisible. `font-display` and the fallback stack never fire,
 * because from the browser's point of view nothing failed. The only way to catch
 * it is to paint a glyph and look at the pixels.
 */
export function FontGuard() {
  useEffect(() => {
    // Only the FIRST family in --font-aktiv is tested. The variable also carries
    // next/font's metric-adjusted fallback, and measuring the whole stack would
    // always find glyphs and never detect the failure.
    const primaryFamily = () => {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--font-aktiv')
        .trim();
      const first = raw.split(',')[0]?.trim().replace(/^['"]|['"]$/g, '');
      return first || null;
    };

    // Safari can refuse canvas pixel reads (fingerprinting protection,
    // backing-store limits) with an InvalidStateError — unverifiable, so
    // treat it as "paints" and leave the font alone.
    const paintsGlyphs = (family: string) => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 48;
        canvas.height = 48;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return true;

        ctx.fillStyle = '#000';
        ctx.textBaseline = 'top';
        ctx.font = `700 32px "${family}"`;
        ctx.fillText('H', 4, 4);

        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] !== 0) return true;
        }
        return false;
      } catch {
        return true;
      }
    };

    let cancelled = false;

    document.fonts.ready.then(() => {
      if (cancelled) return;
      const family = primaryFamily();
      // An unresolvable family makes canvas silently substitute a default face,
      // which paints — so a missing variable can never trigger a false positive.
      if (!family) return;
      const paints = paintsGlyphs(family);
      if (paints) return;

      document.documentElement.classList.add('kq-font-broken');
      console.warn(
        `[FontGuard] "${family}" loaded but painted no glyphs — using system fonts. ` +
          'Usually a corrupted OS font cache; on macOS: sudo atsutil databases -remove, then reboot.'
      );
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
